import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { AuthContext } from './components/auth/authProvider'
import { API } from './api'
import { FeatureCollection } from 'geojson'

import type {
  IBookmark,
  IBookmarks,
  IFeatureLayer,
  IMapPin,
  IUserData,
  Message
} from './typings/common'

type PopoverData = {
  userId: string
  postId: string
}

export type PlaceDetailsProps = {
  bookName: string
  name: string
  id: string
  iconType: string
  bookAuthors: string[]
  isUserGeneratedLocation: boolean
  properties: any
}
type MyContextType = {
  bookmarksLoading: boolean
  locations: FeatureCollection | undefined
  setUserData: (userData: IUserData | null) => void
  setIsAuthenticated: (isAuthenticated: boolean | null) => void
  setLocations: (locations: FeatureCollection) => void
  activePopover: string
  onTogglePopover: (c: string, data?: any, force?: boolean) => void
  chartsView: {
    chartsAreVisible: boolean | false
    chartsAreLoaded: boolean
  }
  setChartsView: (c: {
    chartsAreVisible: boolean | false
    chartsAreLoaded: boolean
  }) => void
  placesSourceQuery: string
  setPlacesSourceQuery: (
    c: string,
    callback?: (c: string) => void | undefined
  ) => void
  userGeneratedLocationsSourceQuery: string
  setUserGeneratedLocationsSourceQuery: (
    c: string,
    callback?: (c: string) => void | undefined
  ) => void
  enlargeModalData: {
    show: boolean
    image: string
    alt: string
  }
  onEnlargeModalClose: () => void
  onEnlargeModalOpen: (image: string, alt: string) => void
  detailsOverlayData: {
    // need to move this to a separate type
    props: Partial<Message> & {
      iconType: string
      isUserGeneratedLocation: boolean
    }
    propsWithoutLinks: {
      bookName: string
      bookAuthors: string[]
      authorImageURL: string
      bookCoverURL: string
      bookURL: string
      bookSubtitle: string
    }
    hasAccess: boolean
  }
  setDetailsOverlayData: (c: {
    props: PlaceDetailsProps
    propsWithoutLinks: {
      bookName: string
      bookAuthors: string[]
      authorImageURL: string
      bookCoverURL: string
      bookURL: string
      bookSubtitle: string
    }
    hasAccess: boolean
  }) => void
  isUserLoaded: boolean | false
  isAuthenticated: boolean | null
  userSubscriptionStatus: {
    hasSubscription: boolean
    trialExpiresIn: number
  } | null
  hasActiveTrial: boolean | null
  setTrialDataViewCounter: (c: number) => void
  setResetHighlightedPin: (
    c: boolean,
    callback?: (c: boolean) => void | undefined
  ) => void
  resetHighlightedPin: boolean
  bookmarks: IBookmarks
  handleBookmarks: (
    action: 'add' | 'remove' | 'remove-all',
    c?: IBookmark,
    callback?: () => Promise<void>
  ) => void
  locationToView: null | IMapPin
  setLocationToView: (c: { id: string; layer: IFeatureLayer } | null) => void
  userHasDismissedCompleteProfilePopup: boolean
  setUserHasDismissedCompleteProfilePopup: (c: boolean) => void
  userData: IUserData | null
  isProfileCompleted: boolean
  popoverData: PopoverData
  setPopoverData: (c: PopoverData) => void
  shouldResetSocialPins: boolean
  setShouldResetSocialPins: (c: boolean) => void
}

const AppContext = React.createContext<MyContextType>({
  locations: undefined,
  setUserData: (userData: IUserData | null) => {},
  bookmarksLoading: false,
  setIsAuthenticated: isAuthenticated => {},
  setLocations: (locations: FeatureCollection) => {},
  activePopover: '',
  onTogglePopover: c => {},
  chartsView: {
    chartsAreVisible: false,
    chartsAreLoaded: false
  },
  setChartsView: (c: {
    chartsAreVisible: boolean | false
    chartsAreLoaded: boolean
  }) => {},
  placesSourceQuery: '',
  setPlacesSourceQuery: (
    c: string,
    callback?: (c: string) => void | undefined
  ) => {},
  userGeneratedLocationsSourceQuery: '',
  setUserGeneratedLocationsSourceQuery: (
    c: string,
    callback?: (c: string) => void | undefined
  ) => {},
  enlargeModalData: {
    show: false,
    image: '',
    alt: ''
  },
  onEnlargeModalClose: () => {},
  onEnlargeModalOpen: (image: string, alt: string) => {},
  detailsOverlayData: {
    props: {
      bookName: '',
      name: '',
      id: '',
      iconType: '',
      bookAuthors: [''],
      isUserGeneratedLocation: false,
      properties: {}
    },
    propsWithoutLinks: {
      bookName: '',
      bookAuthors: [''],
      authorImageURL: '',
      bookCoverURL: '',
      bookURL: '',
      bookSubtitle: ''
    },
    hasAccess: false
  },
  setDetailsOverlayData: (c: {
    props: PlaceDetailsProps
    propsWithoutLinks: {
      bookName: string
      bookAuthors: string[]
      authorImageURL: string
      bookCoverURL: string
      bookURL: string
      bookSubtitle: string
    }
    hasAccess: boolean
  }) => {},
  isUserLoaded: false,
  isAuthenticated: null,
  userSubscriptionStatus: null,
  hasActiveTrial: null,
  setTrialDataViewCounter: (c: number) => {},
  setResetHighlightedPin: (
    c,
    callback?: (c: boolean) => void | undefined
  ) => {},
  resetHighlightedPin: false,
  bookmarks: [],
  handleBookmarks: (action, c, callback) => {},
  locationToView: null,
  setLocationToView: (c: { id: string; layer: string } | null) => {},
  userHasDismissedCompleteProfilePopup: false,
  setUserHasDismissedCompleteProfilePopup: (c: boolean) => {},
  userData: {
    id: null,
    open_id: null,
    email: null,
    imported_at: null, // ISO date string
    first_name: null,
    last_name: null,
    created_by: null,
    sub: null,
    updated_at: null, // ISO date string
    disabled: false,
    profile_bio: null,
    profile_home_port: null,
    profile_boat_name: null,
    profile_boat_type: null,
    profile_boat_model: null,
    profile_boat_year: null,
    profile_boat_length: null,
    profile_boat_draft: null,
    profile_phone: null,
    profile_about: null,
    photo: null,
    privacy_profile: null,
    privacy_contact: null,
    privacy_boat: null
  },
  isProfileCompleted: false,
  popoverData: {
    userId: '',
    postId: ''
  },
  setPopoverData: (c: PopoverData) => {},
  shouldResetSocialPins: false,
  setShouldResetSocialPins: (c: boolean) => {}
})

interface IAppContextProvider {
  children: React.ReactNode
}

const AppContextProvider: React.FC<IAppContextProvider> = ({ children }) => {
  // Get query parameters from the URL
  const urlSearchParams = new URLSearchParams(window.location.search)
  const params = Object.fromEntries(urlSearchParams.entries())
  const [locations, setLocations] = useState<FeatureCollection>()
  const [bookmarksLoading, setBookmarksLoading] = useState<boolean>(false)
  // permalink is handled in the Explore component
  const { postId, postAuthorId } = params
  const [activePopover, setActivePopover] = useState('')
  const [chartsView, setChartsView] = useState({
    chartsAreVisible: false,
    chartsAreLoaded: false
  })
  const [placesSourceQuery, setPlacesSourceQuery] = useState('')
  const [
    userGeneratedLocationsSourceQuery,
    setUserGeneratedLocationsSourceQuery
  ] = useState('')
  const [enlargeModalData, setEnlargeModalData] = useState({
    show: false,
    image: '',
    alt: ''
  })
  const [detailsOverlayData, setDetailsOverlayData] = useState({
    props: {
      bookName: '',
      name: '',
      id: '',
      iconType: '',
      bookAuthors: [''],
      isUserGeneratedLocation: false,
      properties: {}
    },
    propsWithoutLinks: {
      bookName: '',
      bookAuthors: [''],
      authorImageURL: '',
      bookCoverURL: '',
      bookURL: '',
      bookSubtitle: ''
    },
    hasAccess: false
  })
  const [isUserLoaded, setIsUserLoaded] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState<{
    hasSubscription: boolean
    trialExpiresIn: number
  } | null>(null)
  const [hasActiveTrial, setHasActiveTrial] = useState<boolean | null>(null)
  const [trialDataViewCounter, setTrialDataViewCounter] = useState<
    number | null
  >(null)
  const [resetHighlightedPin, setResetHighlightedPin] = useState(false)
  const [bookmarks, setBookmarks] = useState<IBookmarks>([])
  const [locationToView, setLocationToView] = useState<IMapPin | null>(null)
  const [userData, setUserData] = useState<IUserData | null>(null)
  const [
    userHasDismissedCompleteProfilePopup,
    setUserHasDismissedCompleteProfilePopup
  ] = useState(false)

  const [popoverData, setPopoverData] = useState<PopoverData>({
    userId: '',
    postId: ''
  })
  const [shouldResetSocialPins, setShouldResetSocialPins] = useState(false)

  const authContext = useContext(AuthContext)
  /*  const socialURL = process.env.REACT_APP_SOCIAL_URL
  const apiUrl = process.env.REACT_APP_SERVER_URL*/

  const lastPopover = useRef('')

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const bookamrkRes = await API.get('/bookmark')
        setBookmarks(bookamrkRes.data.message)
      } catch (e) {
        console.error(`Error when trying to fetch bookamrs: ${e}`)
      }
    }
    isAuthenticated && fetchBookmarks().then()
  }, [isAuthenticated])

  useLayoutEffect(() => {
    const _userAuthenticated = authContext.getAccessToken()
    if (_userAuthenticated) {
      setIsAuthenticated(_userAuthenticated)
    }
  }, [authContext])

  useEffect(() => {
    ;(async () => {
      async function getUserProfile() {
        try {
          const { data } = await API.get('/profile')

          const isCompleted = !!data?.message

          setUserData(data.message)
          // If a social post is included in the URL then we need to open it
          if (isCompleted) {
            if (postAuthorId) {
              setPopoverData({
                userId: postAuthorId,
                postId: postId || ''
              })

              setActivePopover('profile')
            }
          } else {
            if (!data?.hasDismissedCompleteProfilePopup) {
              setActivePopover('complete-profile')
            } else {
              setUserHasDismissedCompleteProfilePopup(true)
            }
          }
        } catch (error) {}
      }

      if (isAuthenticated) {
        /*const userSubscriptionStatus = await getUserSubscriptionStatus()*/
        try {
          const { data } = await API.get('/subscription-status')

          const status: {
            hasSubscription: boolean
            trialExpiresIn: number
          } = data.message

          const subscriptionStatus = status

          setUserSubscriptionStatus(subscriptionStatus)

          if (subscriptionStatus && !subscriptionStatus?.hasSubscription) {
            setHasActiveTrial(true)
            setActivePopover('trial')
          } else {
            setHasActiveTrial(false)
            setActivePopover('profile')
          }

          getUserProfile().then()
          setIsUserLoaded(true)
        } catch (e) {
          setHasActiveTrial(false)
          setActivePopover('trial')
          setIsUserLoaded(true)
        }
      }
    })()
  }, [isAuthenticated, postId, postAuthorId])

  const handleBookmarks = async (
    action: string,
    bookmark?: IBookmark,
    callback?: () => Promise<void>
  ) => {
    if (!bookmark) return

    setBookmarksLoading(true)

    if (action === 'add') {
      try {
        await API.post('/bookmark', bookmark)

        const { data } = await API.get('/bookmark')
        setBookmarks(data.message)
        setBookmarksLoading(false)
        await callback?.()
      } catch (e) {
        setBookmarksLoading(false)
        console.error(`Error creating bookmark: ${e}`)
      }
    }

    if (action === 'remove') {
      try {
        await API.delete('/bookmark', { params: { id: bookmark?.id } })
        const { data } = await API.get('/bookmark')
        setBookmarks(data.message)
        setBookmarksLoading(false)
        await callback?.()
      } catch (e) {
        setBookmarksLoading(false)
        console.error(`Error deleting bookmark: ${e}`)
      }
    }
  }

  const isProfileCompleted = Boolean(true)

  return (
    <AppContext.Provider
      value={{
        locations,
        setUserData,
        setIsAuthenticated,
        setLocations,
        activePopover,
        onTogglePopover: (popoverName: string, data?: any, force?: boolean) => {
          const newPopoverValue = force
            ? popoverName
            : activePopover === popoverName
            ? ''
            : popoverName
          if (data) {
            setPopoverData(data)
          } else {
            setPopoverData({
              userId: '',
              postId: ''
            })
          }
          setActivePopover(newPopoverValue)

          if (
            (lastPopover.current === 'place-detailed' ||
              lastPopover.current === 'profile') &&
            newPopoverValue !== 'place-detailed' &&
            newPopoverValue !== 'profile'
          ) {
            setResetHighlightedPin(true)
          }
          lastPopover.current = newPopoverValue
        },
        chartsView,
        setChartsView,
        placesSourceQuery,
        setPlacesSourceQuery,
        userGeneratedLocationsSourceQuery,
        setUserGeneratedLocationsSourceQuery,
        enlargeModalData,
        onEnlargeModalClose: () =>
          setEnlargeModalData({ show: false, image: '', alt: '' }),
        onEnlargeModalOpen: (image, alt) => {
          setEnlargeModalData({ show: true, image, alt })
        },
        detailsOverlayData,
        setDetailsOverlayData,
        isUserLoaded,
        isAuthenticated,
        userSubscriptionStatus,
        hasActiveTrial,
        setTrialDataViewCounter,
        setResetHighlightedPin,
        resetHighlightedPin,
        bookmarks,
        bookmarksLoading,
        handleBookmarks,
        locationToView,
        setLocationToView,
        userHasDismissedCompleteProfilePopup,
        setUserHasDismissedCompleteProfilePopup,
        isProfileCompleted,
        userData,
        popoverData,
        setPopoverData,
        shouldResetSocialPins,
        setShouldResetSocialPins
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export { AppContext, AppContextProvider }
